import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  isSupportedLocale,
  SUPPORTED_LOCALES,
  type LocaleTag,
} from "./locales";

export type LocaleResolutionSource =
  | "dev_override"
  | "user"
  | "clerk"
  | "accept_language"
  | "device"
  | "default";

export type ResolvedLocale = {
  locale: LocaleTag;
  source: LocaleResolutionSource;
};

function languageSubtag(tag: string): string {
  return tag.split("-")[0]?.toLowerCase() ?? tag.toLowerCase();
}

/** Map a BCP 47 tag to a supported locale, or null if unsupported. */
export function normalizeLocaleTag(tag: string | null | undefined): LocaleTag | null {
  if (!tag) {
    return null;
  }
  const trimmed = tag.trim();
  if (!trimmed) {
    return null;
  }
  if (isSupportedLocale(trimmed)) {
    return trimmed;
  }
  const languageOnly = languageSubtag(trimmed);
  const match = SUPPORTED_LOCALES.find(
    (candidate) => languageSubtag(candidate) === languageOnly,
  );
  return match ?? null;
}

/**
 * Fallback chain: requested locale → language-only match → `en-US`.
 */
export function resolveLocaleTag(tag: string | null | undefined): LocaleTag {
  return normalizeLocaleTag(tag) ?? FALLBACK_LOCALE;
}

export type LocaleResolutionInput = {
  devOverride?: string | null;
  userLocale?: string | null;
  clerkLocale?: string | null;
  acceptLanguage?: string | null;
  deviceLocale?: string | null;
};

/**
 * Priority: dev override → user setting → Clerk metadata → Accept-Language/device → default.
 */
export function resolveLocale(input: LocaleResolutionInput): ResolvedLocale {
  const dev = normalizeLocaleTag(input.devOverride);
  if (dev) {
    return { locale: dev, source: "dev_override" };
  }

  const user = normalizeLocaleTag(input.userLocale);
  if (user) {
    return { locale: user, source: "user" };
  }

  const clerk = normalizeLocaleTag(input.clerkLocale);
  if (clerk) {
    return { locale: clerk, source: "clerk" };
  }

  const accept = parseAcceptLanguage(input.acceptLanguage);
  if (accept) {
    return { locale: accept, source: "accept_language" };
  }

  const device = normalizeLocaleTag(input.deviceLocale);
  if (device) {
    return { locale: device, source: "device" };
  }

  return { locale: DEFAULT_LOCALE, source: "default" };
}

/** Parse the first supported tag from an HTTP Accept-Language header. */
export function parseAcceptLanguage(header: string | null | undefined): LocaleTag | null {
  if (!header) {
    return null;
  }
  const parts = header.split(",");
  for (const part of parts) {
    const tag = part.trim().split(";")[0]?.trim();
    const normalized = normalizeLocaleTag(tag);
    if (normalized) {
      return normalized;
    }
  }
  return null;
}
